import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { TYPE_LABELS } from "@/constants/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const count = items.length;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5500);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (next: number) => setIndex(((next % count) + count) % count);

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      aria-label="焦点推荐"
    >
      <div className="relative h-[440px] w-full md:h-[560px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.cover}
              alt={`${item.title} 焦点推荐`}
              title={`${item.title} - 星空传媒焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/20" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-white">
              <i className="ri-fire-line"></i>
              焦点推荐 · {TYPE_LABELS[items[index].type]}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-white md:text-5xl">
              {items[index].title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-primary-100">
              <span className="flex items-center gap-1 text-accent-300">
                <i className="ri-star-fill text-xs"></i>
                {items[index].rating.toFixed(1)}
              </span>
              <span>{items[index].year}</span>
              <span className="h-3 w-px bg-primary-700" />
              <span>{items[index].region}</span>
              <span className="h-3 w-px bg-primary-700" />
              <span>{items[index].genres.join(" · ")}</span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-primary-100/90 md:line-clamp-3 md:text-base">
              {items[index].summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${items[index].id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600 cursor-pointer"
              >
                <i className="ri-information-line text-base"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${items[index].type}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 cursor-pointer"
              >
                更多同类作品
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-6 bg-accent-500" : "w-3 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}